import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { analytics } from '../utils/analytics';
import { Check, Zap, Star, ArrowRight, Loader2, Sparkles, ShieldCheck, Lock, RotateCcw } from 'lucide-react';
import FoundingCountdown from '../components/FoundingCountdown';

import { logger } from '../utils/logger';
import { API } from '../utils/apiBase';


// Motion variants
const fadeInUp = { opacity: 0, y: 20 };
const fadeInUpVisible = { opacity: 1, y: 0 };
const scrollTransition = { duration: 0.5 };
const scrollViewport = { once: true };

// Plan data — supports both monthly and yearly with plan_key mapping to
// Stripe subscription prices created at server startup.
const PLANS = [
  {
    id: 'free',
    name: 'Free',
    monthlyPrice: 0,
    yearlyPrice: 0,
    description: 'Perfect for trying out Ascend',
    badge: 'FREE FOREVER',
    badgeColor: '#10b981',  // emerald — calm, no-pressure
    features: [
      '1 resume per month',
      'AI-powered ATS scoring',
      'Keyword matching',
      '1 cover letter / month',
      '1 interview prep / month',
      '3 AI rewrites / month',
      '5 Sage messages / day',
      '2 Resume Studio themes',
    ],
    popular: false,
    planKeyMonthly: null,
    planKeyYearly: null,
  },
  {
    id: 'basic',
    name: 'Basic',
    monthlyPrice: 19,
    yearlyPrice: 190,
    description: 'For active job seekers',
    badge: 'BEST VALUE',
    badgeColor: '#f59e0b',  // amber — value/deal cue
    features: [
      '10 resumes per month',
      'AI-powered ATS scoring',
      'Advanced keyword matching',
      'Detailed improvement suggestions',
      'Cover Letter Generator',
      'LinkedIn Profile Optimizer',
      'Interview Prep',
      '10 AI rewrites per month',
      'All 12 Resume Studio themes',
      'Application Tracker',
      'Download as PDF + .docx',
    ],
    popular: false,
    planKeyMonthly: 'basic_monthly',
    planKeyYearly: 'basic_yearly',
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyPrice: 39,
    yearlyPrice: 390,
    description: 'For serious job hunters',
    badge: 'MOST POPULAR',
    badgeColor: '#002fa7',  // brand blue — flagship/recommended
    features: [
      'Unlimited resumes',
      'AI-powered ATS scoring',
      'Advanced keyword matching',
      'Unlimited AI Resume Rewrites',
      'Unlimited Cover Letters',
      'Unlimited Interview Prep',
      'Unlimited Sage AI Career Coach',
      'LinkedIn Profile Optimizer',
      'Job Description Matcher',
      'All 12 Resume Studio themes',
      'Clean PDF + .docx exports',
      'Application Tracker',
      'Priority support',
    ],
    popular: true,
    planKeyMonthly: 'pro_monthly',
    planKeyYearly: 'pro_yearly',
  },
];

const CREDIT_PACKS = [
  {
    id: 'credits_10',
    name: '10 Credits',
    price: 9,
    credits: 10,
    package: 'credits_10',
    popular: false,
  },
  {
    id: 'credits_50',
    name: '50 Credits',
    price: 39,
    credits: 50,
    package: 'credits_50',
    popular: true,
  },
];

const FAQ_ITEMS = [
  {
    id: 'faq-1',
    q: 'What counts as a "resume"?',
    a: 'Each unique resume you create or upload counts as one resume. You can edit the same resume as many times as you like without using additional credits.',
  },
  {
    id: 'faq-2',
    q: 'Can I cancel my subscription?',
    a: 'Yes, you can cancel anytime. Your subscription will remain active until the end of your billing period.',
  },
  {
    id: 'faq-3',
    q: 'Do credits expire?',
    a: 'Credits never expire. Use them whenever you need them.',
  },
  {
    id: 'faq-4',
    q: 'What payment methods do you accept?',
    a: 'We accept all major credit cards through our secure Stripe payment system.',
  },
  {
    id: 'faq-5',
    q: 'Do I need a promo code for the Founding Member discount?',
    a: 'No code needed. While Founding Member seats are available, the 40% discount on Yearly Pro is automatically applied on the Stripe checkout page. You\'ll see the discounted total before you confirm payment.',
  },
];

// Plan Card Component
const PlanCard = ({ plan, billingPeriod, isAuthenticated, loading, onCheckout, index }) => {
  const isYearly = billingPeriod === 'yearly';
  const displayPrice = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
  const periodLabel = plan.monthlyPrice === 0 ? 'forever' : (isYearly ? 'year' : 'month');
  const monthlyEquivalent = isYearly && plan.monthlyPrice > 0
    ? (plan.yearlyPrice / 12).toFixed(2)
    : null;
  const yearlySavings = plan.monthlyPrice > 0
    ? (plan.monthlyPrice * 12 - plan.yearlyPrice)
    : 0;
  const planKey = isYearly ? plan.planKeyYearly : plan.planKeyMonthly;
  const hasTrial = isYearly && planKey; // 14-day trial on yearly paid plans

  const getCta = () => {
    if (!planKey) return isAuthenticated ? 'Current Plan' : 'Get Started';
    if (hasTrial) return `Start 14-Day Free Trial`;
    return `Upgrade to ${plan.name}`;
  };
  const cardTransition = { ...scrollTransition, delay: index * 0.1 };

  return (
    <motion.div
      initial={fadeInUp}
      animate={fadeInUpVisible}
      transition={cardTransition}
      className={`p-8 border-r border-b border-[#e5e7eb] last:border-r-0 relative ${
        plan.popular ? 'bg-[#0f1115] text-white' : 'bg-white'
      }`}
      data-testid={`plan-${plan.name.toLowerCase()}`}
    >
      {plan.badge && (
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-4 py-1 text-white text-xs font-semibold uppercase tracking-wider"
          style={{ backgroundColor: plan.badgeColor || '#002fa7' }}
          data-testid={`plan-${plan.name.toLowerCase()}-badge`}
        >
          {plan.badge}
        </div>
      )}
      <div className="flex items-center space-x-2 mb-4">
        {plan.popular && <Star size={20} fill="#eab308" color="#eab308" />}
        <h3 className="font-satoshi text-2xl font-bold">{plan.name}</h3>
      </div>
      <div className="mb-2">
        <span className="text-5xl font-satoshi font-black">${displayPrice}</span>
        <span className={`text-sm ${plan.popular ? 'text-[#9ca3af]' : 'text-[#6b7280]'}`}>
          /{periodLabel}
        </span>
      </div>
      {monthlyEquivalent && (
        <p className={`text-xs mb-2 ${plan.popular ? 'text-[#9ca3af]' : 'text-[#6b7280]'}`}>
          Just ${monthlyEquivalent}/month, billed annually
        </p>
      )}
      {isYearly && yearlySavings > 0 && (
        <span className={`inline-block px-2 py-0.5 text-xs font-semibold mb-3 ${plan.popular ? 'bg-[#10b981] text-white' : 'bg-[#ecfdf5] text-[#065f46]'}`}>
          Save ${yearlySavings}/year
        </span>
      )}
      {hasTrial && (
        <p className={`text-xs mb-2 font-semibold ${plan.popular ? 'text-[#10b981]' : 'text-[#002fa7]'}`}>
          ✓ 14-day free trial — no charge until day 14
        </p>
      )}
      <p className={`mb-6 ${plan.popular ? 'text-[#9ca3af]' : 'text-[#6b7280]'}`}>
        {plan.description}
      </p>
      <ul className="space-y-3 mb-8">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-center space-x-3">
            <Check size={18} className={plan.popular ? 'text-[#10b981]' : 'text-[#002fa7]'} />
            <span className="text-sm">{feature}</span>
          </li>
        ))}
      </ul>
      {planKey ? (
        <>
          <button
            onClick={() => onCheckout(planKey, plan.id)}
            disabled={loading === planKey}
            className={`w-full py-3 font-semibold transition-colors flex items-center justify-center space-x-2 ${
              plan.popular
                ? 'bg-white text-[#0f1115] hover:bg-[#f4f5f7]'
                : 'btn-primary'
            } disabled:opacity-50`}
            data-testid={`checkout-${planKey}`}
          >
            {loading === planKey ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <>
                <span>{getCta()}</span>
                <ArrowRight size={18} />
              </>
            )}
          </button>
          {/* Per-card trust chip — directly under the CTA where buyer
              doubt peaks. Stripe + cancel-anytime + refund in one row. */}
          <div
            className={`mt-3 flex items-center justify-center gap-x-3 gap-y-1 flex-wrap text-[10px] ${plan.popular ? 'text-[#9ca3af]' : 'text-[#6b7280]'}`}
            data-testid={`plan-${plan.name.toLowerCase()}-guarantee`}
          >
            <span className="inline-flex items-center gap-1">
              <Lock size={10} className={plan.popular ? 'text-[#10b981]' : 'text-[#10b981]'} />
              Secured by Stripe
            </span>
            <span className="inline-flex items-center gap-1">
              <RotateCcw size={10} className="text-[#10b981]" />
              Cancel anytime
            </span>
          </div>
        </>
      ) : (
        <Link
          to={isAuthenticated ? '/dashboard' : '/register'}
          className="w-full py-3 font-semibold transition-colors flex items-center justify-center space-x-2 border-2 border-[#e5e7eb] text-[#4b5563] hover:bg-[#f4f5f7]"
          data-testid="free-cta"
        >
          <span>{getCta()}</span>
        </Link>
      )}
    </motion.div>
  );
};

// Credit Pack Card Component
const CreditPackCard = ({ pack, loading, onCheckout, index }) => {
  const cardTransition = { ...scrollTransition, delay: index * 0.1 };
  return (
  <motion.div
    initial={fadeInUp}
    whileInView={fadeInUpVisible}
    transition={cardTransition}
    viewport={scrollViewport}
    className={`dashboard-card p-6 ${pack.popular ? 'border-2 border-[#002fa7]' : ''}`}
    data-testid={`credit-pack-${pack.credits}`}
  >
    {pack.popular && (
      <span className="text-xs uppercase tracking-wider font-semibold text-[#002fa7] mb-2 block">
        Best Value
      </span>
    )}
    <div className="flex items-center justify-between mb-4">
      <div>
        <h3 className="font-satoshi text-xl font-bold text-[#0f1115]">{pack.name}</h3>
        <p className="text-sm text-[#6b7280]">${(pack.price / pack.credits).toFixed(2)} per credit</p>
      </div>
      <div className="text-right">
        <span className="text-3xl font-satoshi font-black text-[#0f1115]">${pack.price}</span>
      </div>
    </div>
    <button
      onClick={() => onCheckout(pack.package)}
      disabled={loading === pack.package}
      className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50"
      data-testid={`checkout-${pack.package}`}
    >
      {loading === pack.package ? (
        <Loader2 size={20} className="animate-spin" />
      ) : (
        <>
          <Zap size={18} />
          <span>Buy {pack.credits} Credits</span>
        </>
      )}
    </button>
  </motion.div>
  );
};

const PricingPage = () => {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(null);
  // Default to Yearly — spec says users shift to yearly at higher rates
  // when it's the pre-selected option, and yearly drives 3.5× LTV.
  const [billingPeriod, setBillingPeriod] = useState(() => {
    const urlParam = new URLSearchParams(window.location.search).get('billing');
    return urlParam === 'monthly' ? 'monthly' : 'yearly';
  });
  const [foundingStats, setFoundingStats] = useState(null);
  const apiRef = useRef(API);
  const viewedRef = useRef(false);

  useEffect(() => {
    analytics.viewPricing();
    // Server-side behavior tracking — fire-and-forget. Powers the
    // cart-abandonment email rule in services/conversion_engine.py.
    // Guard against StrictMode dev double-mount so we don't double-count.
    if (!viewedRef.current) {
      viewedRef.current = true;
      axios.post(`${apiRef.current}/events/track`, { event_type: 'viewed_pricing' },
        { withCredentials: true }).catch(() => {});
    }
    // Fetch Founding Member stats for the prominent urgency block above the plans.
    (async () => {
      try {
        const res = await axios.get(`${apiRef.current}/payments/founding-member-stats`);
        setFoundingStats(res.data);
      } catch (_) { /* non-critical */ }
    })();
  }, []);

  const toggleBillingPeriod = useCallback((next) => {
    setBillingPeriod(next);
    if (window.gtag) {
      window.gtag('event', 'toggle_billing_period', { period: next });
    }
  }, []);

  const handleCreditCheckout = useCallback(async (packageType) => {
    const pack = CREDIT_PACKS.find((p) => p.package === packageType);
    const price = pack?.price ?? 0;
    analytics.initiateCheckout(packageType, price);
    if (!isAuthenticated) {
      window.location.href = '/register';
      return;
    }
    setLoading(packageType);
    try {
      const response = await axios.post(
        `${apiRef.current}/payments/checkout`,
        { package_type: packageType, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = response.data.url;
    } catch (err) {
      logger.error('Credit checkout failed:', err.message);
      setLoading(null);
    }
  }, [isAuthenticated]);

  const handleSubscribeCheckout = useCallback(async (planKey, tierId) => {
    const plan = PLANS.find((p) => p.id === tierId);
    const price = billingPeriod === 'yearly' ? plan?.yearlyPrice : plan?.monthlyPrice;
    analytics.initiateCheckout(planKey, price ?? 0);
    if (window.gtag && billingPeriod === 'yearly') {
      window.gtag('event', 'begin_trial', { plan: planKey });
    }
    if (!isAuthenticated) {
      window.location.href = `/register?plan=${planKey}`;
      return;
    }
    setLoading(planKey);
    try {
      // Pass through ?promo=… from the URL so backend can auto-apply campaign
      // discounts (e.g. WINBACK50 from the Day-7 nurture email).
      const promo = new URLSearchParams(window.location.search).get('promo') || null;
      const response = await axios.post(
        `${apiRef.current}/payments/subscribe`,
        { plan_key: planKey, origin_url: window.location.origin, promo },
        { withCredentials: true }
      );
      window.location.href = response.data.url;
    } catch (err) {
      logger.error('Subscribe checkout failed:', err?.response?.data?.detail || err.message);
      setLoading(null);
    }
  }, [isAuthenticated, billingPeriod]);

  return (
    <div className="min-h-screen pt-20" data-testid="pricing-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 border-b border-[#e5e7eb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7] mb-4 block">
            PRICING
          </span>
          <h1 className="font-satoshi text-4xl sm:text-5xl font-black tracking-tighter text-[#0f1115] mb-4">
            Simple, transparent pricing
          </h1>
          <p className="text-lg text-[#4b5563] max-w-2xl mx-auto">
            Choose the plan that fits your job search. All plans include AI-powered ATS optimization.
          </p>
        </div>
      </section>

      {/* Plans */}
      <section className="py-16 lg:py-24 bg-[#f4f5f7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* WINBACK50 nudge — fires when user lands here from the Day-7 nurture email. */}
          {(() => {
            const promo = new URLSearchParams(window.location.search).get('promo');
            if (promo !== 'WINBACK50') return null;
            return (
              <div
                className="mb-8 p-5 bg-[#fef3c7] border-l-4 border-[#dc2626] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
                data-testid="winback-banner"
              >
                <div>
                  <p className="text-[#0f1115] font-bold text-base mb-1">
                    🎁 50% off your first month — auto-applied at checkout
                  </p>
                  <p className="text-sm text-[#7c2d12]">
                    Your promo code <span className="font-mono font-bold bg-[#0f1115] text-[#fbbf24] px-2 py-0.5">WINBACK50</span> will be applied automatically. One-time use, valid 30 days.
                  </p>
                </div>
              </div>
            );
          })()}
          {/* FOUNDING MEMBER urgency block — renders only while campaign active */}
          {foundingStats?.active && (
            <div
              className="mb-10 p-6 lg:p-8 bg-gradient-to-br from-[#0b1120] via-[#002fa7] to-[#0b1120] text-white relative overflow-hidden"
              data-testid="founding-hero-card"
            >
              <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: 'radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 80% 60%, white 1px, transparent 1px)',
                backgroundSize: '40px 40px, 60px 60px',
              }} />
              <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                <div className="flex-1">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#fde68a] text-[#0f1115] text-xs font-bold uppercase tracking-wider mb-3">
                    <Sparkles size={14} />
                    Founding Member Launch
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold mb-2">
                    Save {foundingStats.discount_pct}% on Yearly Pro &mdash; forever locked for your first year
                  </h3>
                  <p className="text-sm text-white/80 max-w-2xl">
                    Reserved for our first <strong className="text-[#fde68a]">100 founding members</strong>. Renewal reverts to full price.
                  </p>
                  <div
                    className="mt-3 inline-flex items-center gap-2 bg-[#fde68a]/15 border border-[#fde68a]/40 px-3 py-1.5 text-xs font-semibold text-[#fde68a]"
                    data-testid="founding-no-code-pill"
                  >
                    <Sparkles size={12} />
                    No promo code needed &mdash; your {foundingStats.discount_pct}% discount is applied automatically at checkout
                  </div>
                  {/* Live countdown — ticks every second, anchored to
                      server's ends_at so timezone tricks can't lie. Hides
                      itself when the campaign expires. */}
                  <FoundingCountdown stats={foundingStats} />
                </div>
              </div>
            </div>
          )}
          {foundingStats && !foundingStats.active && foundingStats.remaining === 0 && (
            <div className="mb-10 p-4 bg-[#f4f5f7] border border-[#e5e7eb] text-center text-sm text-[#6b7280]" data-testid="founding-sold-out">
              Founding Member seats are all gone &mdash; thanks to our launch supporters! Regular pricing applies below.
            </div>
          )}

          {/* Billing period toggle */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex border border-[#e5e7eb] bg-white p-1" role="tablist" aria-label="Billing period">
              <button
                type="button"
                onClick={() => toggleBillingPeriod('monthly')}
                role="tab"
                aria-selected={billingPeriod === 'monthly'}
                className={`px-5 py-2 text-sm font-semibold transition-colors ${
                  billingPeriod === 'monthly'
                    ? 'bg-[#002fa7] text-white'
                    : 'text-[#4b5563] hover:text-[#0f1115]'
                }`}
                data-testid="billing-toggle-monthly"
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => toggleBillingPeriod('yearly')}
                role="tab"
                aria-selected={billingPeriod === 'yearly'}
                className={`px-5 py-2 text-sm font-semibold transition-colors flex items-center gap-2 ${
                  billingPeriod === 'yearly'
                    ? 'bg-[#002fa7] text-white'
                    : 'text-[#4b5563] hover:text-[#0f1115]'
                }`}
                data-testid="billing-toggle-yearly"
              >
                Yearly
                <span className={`text-[10px] px-1.5 py-0.5 font-bold ${billingPeriod === 'yearly' ? 'bg-[#10b981] text-white' : 'bg-[#ecfdf5] text-[#065f46]'}`}>
                  SAVE 17%
                </span>
              </button>
            </div>
          </div>

          {billingPeriod === 'yearly' && (
            <p className="text-center text-sm text-[#4b5563] mb-6" data-testid="yearly-trial-banner">
              ✨ Yearly plans include a <strong>14-day free trial</strong>. No charge until day 14. Cancel anytime.
              {foundingStats?.active && (
                <>
                  {' '}<span className="text-[#002fa7] font-semibold" data-testid="yearly-auto-discount-note">
                    Founding Member {foundingStats.discount_pct}% off is automatically applied at checkout &mdash; no code required.
                  </span>
                </>
              )}
            </p>
          )}

          {/* Trust strip — sits directly above the plan grid where buyer
              friction is highest. Three signals: payment safety, refund
              policy, no-lock-in. Mirrors the in-card chip but with bigger
              copy for at-a-glance reassurance. */}
          <div
            className="mb-8 flex items-center justify-center gap-3 sm:gap-6 flex-wrap text-xs sm:text-sm text-[#4b5563]"
            data-testid="pricing-trust-strip"
          >
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck size={16} className="text-[#10b981]" />
              <span className="font-semibold">Secured by Stripe</span>
            </span>
            <span className="hidden sm:inline-block w-px h-4 bg-[#e5e7eb]" aria-hidden="true" />
            {billingPeriod === 'yearly' && (
              <>
                <span className="inline-flex items-center gap-1.5">
                  <RotateCcw size={16} className="text-[#10b981]" />
                  <span className="font-semibold">14-day free trial on yearly</span>
                </span>
                <span className="hidden sm:inline-block w-px h-4 bg-[#e5e7eb]" aria-hidden="true" />
              </>
            )}
            <span className="inline-flex items-center gap-1.5">
              <Lock size={16} className="text-[#10b981]" />
              <span className="font-semibold">Cancel anytime, no questions</span>
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#e5e7eb]">
            {PLANS.map((plan, index) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                billingPeriod={billingPeriod}
                isAuthenticated={isAuthenticated}
                loading={loading}
                onCheckout={handleSubscribeCheckout}
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Credit Packs */}
      <section className="py-16 lg:py-24 border-b border-[#e5e7eb]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7] mb-4 block">
              ONE-TIME PURCHASE
            </span>
            <h2 className="font-satoshi text-3xl font-bold tracking-tight text-[#0f1115] mb-4">
              Need more flexibility?
            </h2>
            <p className="text-[#4b5563]">
              Purchase credit packs for one-time resume optimizations
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {CREDIT_PACKS.map((pack, index) => (
              <CreditPackCard
                key={pack.id}
                pack={pack}
                loading={loading}
                onCheckout={handleCreditCheckout}
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-satoshi text-3xl font-bold tracking-tight text-[#0f1115]">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-6">
            {FAQ_ITEMS.map((faq) => (
              <div key={faq.id} className="border-b border-[#e5e7eb] pb-6">
                <h3 className="font-satoshi text-lg font-bold text-[#0f1115] mb-2">{faq.q}</h3>
                <p className="text-[#4b5563]">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
